import { request } from 'sonata-react-components';
import { getCartographyLevels } from 'sonata-static-storage/lib/entities';
import { normalizeCountryToCca3 } from 'sonata-static-storage/lib/helpers/countries.helpers';

import type { Point } from 'geojson';
import type { CartographyLevelConfig } from '../domain/cartographyGrouping';

/** Tags of a geometry: area codes by cartography level (e.g. `{ level0: ['ESP'], level1: ['MD'] }`). */
export type CartographyTags = Record<string, string[]>;

export interface LatLng {
  lat: number;
  lng: number;
}

interface DictionaryCountry {
  id?: string | null;
  tagsConfig?: (CartographyLevelConfig | null)[] | null;
}

/** The cartography endpoints expect lower-case CCA3 country codes. */
const toCountryParam = (country: string): string => normalizeCountryToCca3(country);

/** `GET cartography/dictionary?countries[]=<cca3>` → the country's `tagsConfig` (every level, unsorted). */
export async function getCountryLevelsConfig(country: string): Promise<(CartographyLevelConfig | null)[]> {
  const countries: DictionaryCountry[] = await request.makeRequest(
    `${taptap.config.backendUrlBase}cartography/dictionary?countries[]=${toCountryParam(country)}`,
    { method: 'GET' }
  );
  return countries[0]?.tagsConfig ?? [];
}

/** `POST cartography/geometries/tags?country=<cca3>` with one GeoJSON point per location → tags in the same order. */
export function getPointsTags(country: string, points: LatLng[]): Promise<CartographyTags[]> {
  const geometries: Point[] = points.map(({ lat, lng }) => ({ coordinates: [lng, lat], type: 'Point' }));
  return request.makeRequest(`${taptap.config.backendUrlBase}cartography/geometries/tags`, {
    data: geometries,
    method: 'POST',
    params: { country: toCountryParam(country) }
  });
}

/** Display name of each area (`POST cartography/shapes` filtered by code): `description`, else `name` (the code). */
export async function getAreaNames(
  country: string,
  level: string,
  areaCodes: string[]
): Promise<Record<string, string>> {
  const shapes = await getCartographyLevels(toCountryParam(country), level, {
    fields: ['description', 'name'],
    filters: { [level]: areaCodes }
  });
  return Object.fromEntries(shapes.map(shape => [shape.name, shape.description || shape.name]));
}
